<template>
    <main
        v-if="categories"
        class="relative pb-10 pt-25 max-lg:overflow-hidden md:pb-15 md:pt-36">
        <div
            :data-cromi-recovery-required="
                !categories.data?.length ? 'pending' : undefined
            "
            class="container">
            <TheBreadcrumbs :items="breadcrumbs" />

            <div
                v-if="categories.data?.length"
                class="mt-5 lg:flex">
                <nav class="relative z-30 w-full lg:max-w-[14rem]">
                    <ul class="sticky top-25 space-y-2 max-lg:hidden">
                        <li
                            v-for="category in categories.data"
                            :key="category.slug">
                            <NuxtLink
                                :to="
                                    getAnchorLink(
                                        '/catalog/' + '#' + category.slug
                                    )
                                "
                                :class="
                                    category.slug === dropdownCategory
                                        ? 'bg-[#3A036B]'
                                        : 'bg-white/5'
                                "
                                class="block rounded-12 p-5 text-16/132">
                                {{ category.name }}
                            </NuxtLink>
                        </li>
                    </ul>
                    <div class="w-full lg:hidden">
                        <AtomDropdown
                            v-model="dropdownCategory"
                            :options="dropdownOptions"
                            class="w-full"
                            @update:model-value="updateCatalog" />
                    </div>
                </nav>

                <LazyApiSection
                    recovery-required
                    :recovery-ready="!!deferredCategories.length"
                    class="w-full scroll-m-25 max-lg:mt-5 lg:ml-5"
                    min-height="24rem"
                    @visible="loadDeferredCatalogContent">
                    <div class="space-y-10 md:space-y-25">
                        <template
                            v-for="category in deferredCategories"
                            :key="category.slug">
                            <template v-if="category">
                                <CatalogCategory
                                    v-if="disableLazyHydration"
                                    :id="category.slug"
                                    class="scroll-m-25"
                                    :direction-data="category" />
                                <LazyHydrationCatalogCategory
                                    v-else
                                    :id="category.slug"
                                    class="scroll-m-25"
                                    :direction-data="category"
                                    :hydrate-on-visible="{
                                        rootMargin: '100px',
                                    }" />
                                <AtomHr class="last-of-type:hidden" />
                            </template>
                        </template>
                    </div>
                </LazyApiSection>
            </div>
        </div>
    </main>
</template>

<script lang="ts" setup>
import AtomDropdown from '~/components/atom/atom-dropdown/AtomDropdown.vue'
import type { IDropdownOption } from '~/components/atom/atom-dropdown/atom-dropdown.types'
import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import CatalogCategory from '~/components/pages/catalog/catalog-category/CatalogCategory.vue'
import type { ICatalogCategory } from '~/components/pages/catalog/catalog-category/catalog-category.types'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'

import { getAnchorLink } from '~/utils/getAnchorLink'
import { defineSeoAggregateRating } from '~/utils/seo'

const route = useRoute()
const router = useRouter()
const config = useRuntimeConfig()
const nuxtApp = useNuxtApp()

defineProps({
    disableLazyHydration: {
        type: Boolean as PropType<boolean>,
        default: false,
    },
})

const { data: categories, error } = await useApiFetch<Page<ICatalogCategory[]>>(
    `/v1/catalogues`,
    {
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
        watch: false,
        useLastModified: true,
    }
)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

const {
    data: deferredCatalogCategories,
    execute: executeDeferredCatalogContent,
} = await useApiFetch<Page<ICatalogCategory[]>>(`/v1/catalogues`, {
    key: 'deferred-catalogues',
    query: {
        include:
            'products,child_category.products,child_category.schema_org,optional_categories.products,schema_org',
    },
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
    server: false,
    immediate: false,
    watch: false,
})

const schemaCatalogCategories = import.meta.server
    ? await $fetch<Page<ICatalogCategory[]>>('/v1/catalogues', {
          baseURL: config.public.api_url,
          headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Office-Cromi-Domain': config.public.website as string,
          },
          query: {
              include:
                  'products,child_category.products,child_category.schema_org,optional_categories.products,schema_org',
          },
      }).catch(() => null)
    : null

const isDeferredCatalogContentLoading = ref(false)
const deferredCategories = computed(
    () => deferredCatalogCategories.value?.data || []
)

async function loadDeferredCatalogContent() {
    if (
        isDeferredCatalogContentLoading.value ||
        deferredCatalogCategories.value
    ) {
        return
    }

    isDeferredCatalogContentLoading.value = true

    await executeDeferredCatalogContent()

    isDeferredCatalogContentLoading.value = false
}

const { activeId } = useScrollspy(
    () => deferredCategories.value.map((category) => category.slug),
    {
        rootMargin: '0px -20% -20% 0px',
    }
)

const LazyHydrationCatalogCategory = defineLazyHydrationComponent(
    'visible',
    () =>
        import(
            '~/components/pages/catalog/catalog-category/CatalogCategory.vue'
        )
)

const dropdownCategory = ref<string | null>(null)

const dropdownOptions = computed<IDropdownOption[]>(() => {
    return (categories.value?.data || []).map((category: ICatalogCategory) => {
        return {
            text: category.name,
            value: category.slug,
        }
    })
})

const activeCategory = computed<ICatalogCategory | null>(() => {
    const hash = route.hash.replace('#', '')
    if (!hash) return categories.value?.data[0] || null

    const directMatch = categories.value?.data.find((c) => c.slug === hash)
    if (directMatch) return directMatch

    const parentOfSubcategory = categories.value?.data.find((category) => {
        const hasChild = category.child_category?.data?.some(
            (sub) => sub.slug === hash
        )
        const hasOptional = category.optional_categories?.data?.some(
            (sub) => sub.slug === hash
        )
        return hasChild || hasOptional
    })

    return parentOfSubcategory || categories.value?.data[0] || null
})

function updateCatalog() {
    router.push(getAnchorLink('/catalog/' + '#' + dropdownCategory.value))
}

const breadcrumbs = computed<BreadcrumbItem[]>(() => [
    {
        label: 'Cromi',
        to: '/',
    },
    {
        label: `Каталог`,
        to: `/catalog`,
    },
])

if (activeCategory.value) {
    useSeoMeta({
        title: activeCategory.value.seo.seo_title,
        description: activeCategory.value.seo.seo_description,
        ogTitle: activeCategory.value.seo.og_title,
        ogDescription: activeCategory.value.seo.og_description,
        ogImage: activeCategory.value.seo.og_image
            ? activeCategory.value.seo.og_image
            : null,
    })
}

onMounted(() => {
    dropdownCategory.value = activeCategory.value?.slug || null
})

watch(activeCategory, (category) => {
    if (!category) return

    useSeoMeta({
        title: category.seo.seo_title,
        description: category.seo.seo_description,
        ogTitle: category.seo.og_title,
        ogDescription: category.seo.og_description,
        ogImage: category.seo.og_image ? category.seo.og_image : null,
    })

    dropdownCategory.value = category.slug
})

function createFlatProductList(schemaCategories: ICatalogCategory[]) {
    return schemaCategories.flatMap((category) =>
        (category.child_category?.data || []).flatMap((childCategory) =>
            (childCategory.products?.data || []).map((product) => {
                return {
                    '@type': 'ListItem',
                    item: {
                        '@type': 'Product',
                        name: product.name,
                        description: `Купить или арендовать ${product.name}`,
                        url: createProductUrl(product.slug),
                        ...(product.preview_image && {
                            image: getMediaLink(product.preview_image.url),
                        }),
                        aggregateRating: defineSeoAggregateRating(),
                        offers: {
                            '@type': 'Offer',
                            price: product.price,
                            priceValidUntil: `${new Date(Date.now()).getFullYear() + 1}-12-31`,
                            // shippingDetails: defineShippingDetails(),
                            // hasMerchantReturnPolicy: defineMerchantReturnPolicy(),
                            priceCurrency: 'RUB',
                            availability: 'https://schema.org/InStock',
                            url: createProductUrl(product.slug),
                        },
                    },
                }
            })
        )
    )
}

if (schemaCatalogCategories?.data) {
    useSchemaOrg([
        defineItemList(
            createCatalogItemListSchema(schemaCatalogCategories.data)
        ),
    ])
}

function createCatalogItemListSchema(schemaCategories: ICatalogCategory[]) {
    return {
        '@type': 'ItemList',
        name: 'Каталог товаров Кроми',
        description: 'Полный перечень товаров и услуг компании Кроми',
        numberOfItems: calculateTotalProducts(schemaCategories),
        itemListElement: createFlatProductList(schemaCategories),
    }
}

function calculateTotalProducts(schemaCategories: ICatalogCategory[]) {
    return schemaCategories.reduce(
        (total, category) =>
            total +
            (category.child_category?.data || []).reduce(
                (childTotal, childCategory) =>
                    childTotal + (childCategory.products?.data.length || 0),
                0
            ),
        0
    )
}

function createProductUrl(slug: string) {
    return `https://www.${config.public.website}/catalog/${slug}/`
}

watch(activeId, () => {
    dropdownCategory.value = activeId.value
})
</script>
